import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import {
    ServiceGroupCard,
    GroupHeader,
    SectionTitle,
    EmptyState
} from '../styles/BillingStyledComponents';
import { parseTierDiscountInfo } from '../utils/billingPolicyTree';
import { ServiceItemNumber } from '../utils/serviceItem';
import { getRoundingModeLabel } from 'components/common/SunnyUtils';
import TierUsageTable from './TierUsageTable';

/**
 * 정책 트리의 그룹 1개를 그리는 컴포넌트.
 *
 * 이번 라운드에서 단순화:
 * - 그룹 헤더: 그룹명 + chevron만 (₩금액, 그룹 보전 배지 모두 제거 — 정산 표에서 다룸)
 * - 항목 행: 항목명 + 사용량 (예: "평균 14.7건 / 최대 17건", "3대") — 금액 표시 없음
 * - 구간별 요금 계산 표(TierUsageTable)는 유지 (이번 달 적용 결과 = 사용자 검증용)
 *
 * 모든 그룹이 동일하게 렌더 — SDK 전용 분기 없음.
 *
 * props:
 *   - group: policyTree.serviceGroupList[i]
 *   - detail: billingDetail
 *   - expanded: boolean
 *   - onToggle: () => void
 */
const ServiceGroupSection = ({ group, detail, expanded, onToggle }) => {
    if (!group) return null;

    const groupName = group.serviceItemName || '그룹';
    const items = group.servicePriceList || [];

    return (
        <ServiceGroupCard>
            <GroupHeader onClick={onToggle}>
                <span className="group-label">
                    <FontAwesomeIcon icon={faLayerGroup} style={{ color: '#3b82f6', fontSize: '0.85rem' }} />
                    {groupName}
                </span>
                <span className="group-amount">
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`expand-icon ${expanded ? 'expanded' : ''}`}
                    />
                </span>
            </GroupHeader>

            {expanded && items.length === 0 && (
                <EmptyState>이용 내역이 없습니다.</EmptyState>
            )}

            {expanded && items.map((item, idx) => (
                <ItemDetailRow key={item.uuid || idx} item={item} detail={detail} />
            ))}
        </ServiceGroupCard>
    );
};

/**
 * 항목 1개의 사용량 정보 표시.
 *
 *  - 헤더: 항목명 + 사용량 (금액 없음)
 *  - 본문 (tier가 있는 경우): 구간별 요금 계산 표
 *  - 본문 (그 외): 비어있음
 *
 * 사용량 매핑:
 *  - SDK Agent (serviceItemNumber === 2): "평균 N건 / 최대 M건"
 *  - 정책서버  (serviceItemNumber === 3): "N대"
 *  - 그 외:                                "사용량 정보 없음"
 */
const ItemDetailRow = ({ item, detail }) => {
    const itemName = item.serviceItemName || '항목';
    const hasTiers = (item.serviceTierPriceList || []).length > 0;
    const usage = getItemUsage(item, detail);

    return (
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px dashed #e2e8f0' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: hasTiers ? '0.75rem' : '0',
                gap: '12px',
                flexWrap: 'wrap'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }} />
                    <span style={{ fontWeight: 700, color: '#334155', fontSize: '0.95rem' }}>{itemName}</span>
                </div>
                <UsageDisplay usage={usage} />
            </div>

            {hasTiers && (() => {
                const tiers = parseTierDiscountInfo(detail?.tierDiscountInfo);
                if (tiers.length === 0) return null;
                return (
                    <>
                        <SectionTitle style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>구간별 요금 계산</SectionTitle>
                        <TierUsageTable
                            tiers={tiers}
                            totalTierDiscount={detail?.totalTierDiscount}
                            totalAppliedFee={detail?.totalAppliedFee}
                            totalUsageFee={detail?.totalUsageFee}
                        />
                    </>
                );
            })()}
        </div>
    );
};

/**
 * 항목별 사용량 데이터 추출. 백엔드 응답값 우선, 없으면 historical fallback.
 *
 * Returns: { raw, rounded, roundingMode, isDecimal } 또는 null (사용량 없음)
 */
function getItemUsage(item, detail) {
    const itemNumber = item?.serviceItemNumber;
    const roundingMode = item?.roundingMode || 'ceil';

    let rawValue = detail?.rawItemCounts?.[String(itemNumber)];
    let roundedValue = detail?.roundedItemCounts?.[String(itemNumber)];

    if (rawValue == null) {
        if (itemNumber === ServiceItemNumber.SDK_AGENT) {
            rawValue = detail?.avgAgentCount;
        } else if (itemNumber === ServiceItemNumber.POLICY_SERVER) {
            rawValue = detail?.policyServerCnt;
        }
    }
    if (rawValue == null) return null;

    const raw = parseFloat(rawValue);
    if (isNaN(raw) || raw <= 0) return null;
    const isDecimal = raw % 1 !== 0;
    const rounded = roundedValue != null && !isNaN(parseFloat(roundedValue))
        ? parseFloat(roundedValue)
        : (isDecimal ? null : raw);
    return { raw, rounded, roundingMode, isDecimal };
}

/**
 * 사용량 표시 컴포넌트 (C안: 배지 분리).
 *
 * - 정수: "163대" 한 줄
 * - 소수: "163대" + [실측 162.5] + [올림] 배지 3개
 */
const UsageDisplay = ({ usage }) => {
    if (!usage) {
        return <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>사용량 정보 없음</span>;
    }
    const { raw, rounded, roundingMode, isDecimal } = usage;
    if (!isDecimal || rounded == null) {
        return <span style={{ color: '#475569', fontWeight: 600, fontSize: '0.9rem' }}>{raw}대</span>;
    }
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ color: '#475569', fontWeight: 700, fontSize: '0.95rem' }}>{rounded}대</span>
            <span
                title="측정된 실제 사용 대수 (정수화 전)"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#475569'
                }}
            >
                실측 {raw}
            </span>
            <span
                title="요금정책의 최종 수량 반영 방법"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    background: '#dbeafe',
                    border: '1px solid #93c5fd',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#1e40af'
                }}
            >
                {getRoundingModeLabel(roundingMode)}
            </span>
        </span>
    );
};

export default ServiceGroupSection;
